// Client for the QA portal API (same-origin /api/* behind CloudFront, Lambda
// digadop-ai-qa-portal-api). Read model: tenant / generation_run / doc_artifact /
// doc_section. Write model: feedback → doc_corrections, plus artifact status
// transitions. See api/index.mjs in this repo for the server side.

export interface Tenant {
  id: number;
  slug: string;
  display_name: string;
  sf_org_id: string | null;
  created_at: string;
}

export interface GenerationRun {
  id: number;
  tenant_id: number;
  tenant_slug: string;
  agent: string;
  kind: 'ingest' | 'generate' | 'regenerate' | 'staleness_scan' | 'data_analysis' | 'promotion';
  status: 'running' | 'succeeded' | 'failed' | 'aborted';
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

export interface DocumentSummary {
  id: number;
  tenant_id: number;
  tenant_slug: string;
  object_api_name: string;
  persona_id: string;
  version: number;
  status: 'generating' | 'in_review' | 'approved' | 'published' | 'stale';
  /** Optional human-friendly name; null falls back to the computed label. */
  title: string | null;
  created_by_run: number | null;
  created_at: string;
  section_count: number;
}

export interface DocSection {
  id: number;
  ordinal: number;
  heading: string;
  content: string;
  status: 'generated' | 'approved' | 'edited' | 'rejected' | 'stale';
  updated_at: string;
}

export interface FeedbackEntry {
  id: number;
  section_id: number | null;
  action: 'edit' | 'reject' | 'comment';
  original_text: string;
  corrected_text: string | null;
  comment: string | null;
  reviewer: string;
  state: 'draft' | 'submitted';
  created_at: string;
}

export interface FeedbackSubmission {
  reviewer: string;
  edits: { sectionId: number; correctedText: string }[];
  comments: { sectionId: number | null; comment: string }[];
  /** true = save as a private draft (not queued for the agent); false/omitted = submit. */
  draft?: boolean;
}

const PORTAL_KEY_STORAGE = 'digadop-portal-key';
const REVIEWER_STORAGE = 'digadop-reviewer-name';

export function getPortalKey(): string {
  return localStorage.getItem(PORTAL_KEY_STORAGE) ?? '';
}
export function setPortalKey(key: string): void {
  localStorage.setItem(PORTAL_KEY_STORAGE, key.trim());
}
export function getReviewerName(): string {
  return localStorage.getItem(REVIEWER_STORAGE) ?? '';
}
export function setReviewerName(name: string): void {
  localStorage.setItem(REVIEWER_STORAGE, name.trim());
}

// CloudFront's Lambda OAC signs origin requests with sigv4. For requests WITH
// a body, the viewer must supply the payload hash itself or CloudFront rejects
// the request before it reaches the Lambda. WebCrypto gives us the hash.
async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`${path}: ${res.status} ${await res.text()}`);
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const payload = JSON.stringify(body);
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-amz-content-sha256': await sha256Hex(payload),
      'x-portal-key': getPortalKey(),
    },
    body: payload,
  });
  if (!res.ok) throw new Error(`${path}: ${res.status} ${await res.text()}`);
  return (await res.json()) as T;
}

export interface PortalUser {
  uid: string;
  oid: string;
  name: string;
  email: string;
}

export const fetchMe = () => get<{ user: PortalUser }>('/api/auth/me');
export const logout = () => post<{ ok: boolean }>('/api/auth/logout', {});
export const fetchHealth = () => get<{ ok: boolean }>('/api/health');
export const fetchTenants = () => get<{ tenants: Tenant[] }>('/api/tenants');
export const fetchRuns = () => get<{ runs: GenerationRun[] }>('/api/runs');
export const fetchDocuments = () => get<{ documents: DocumentSummary[] }>('/api/documents');
export const fetchDocument = (id: number | string) =>
  get<{ document: DocumentSummary; sections: DocSection[] }>(`/api/documents/${id}`);
export const fetchFeedback = (id: number | string) =>
  get<{ feedback: FeedbackEntry[] }>(`/api/documents/${id}/feedback`);
export const submitFeedback = (id: number | string, submission: FeedbackSubmission) =>
  post<{ ok: boolean; inserted: number; state: 'draft' | 'submitted' }>(
    `/api/documents/${id}/feedback`,
    submission,
  );
export const approveDocument = (id: number | string, reviewer: string) =>
  post<{ ok: boolean; status: string }>(`/api/documents/${id}/approve`, { reviewer });
export const setDocumentTitle = (id: number | string, title: string) =>
  post<{ ok: boolean; title: string | null }>(`/api/documents/${id}/title`, { title });
export const regenerateDocument = (id: number | string, reviewer: string) =>
  post<{ ok: boolean; runId: number; status: string }>(`/api/documents/${id}/regenerate`, {
    reviewer,
  });

// ---- Personas (digadop-ai#36): catalog + profile->persona mappings ----

/** A persona as the tenant sees it (merged catalog, migration 11). */
export interface PersonaCatalogEntry {
  id: number;
  key: string;
  name: string;
  definition: string;
  base_persona_id: number | null;
  source: 'standard' | 'human' | 'inferred';
  is_active: boolean;
  /** True when this is the global library row (no tenant override). */
  is_standard: boolean;
  /** True when a tenant row overrides a standard key (clone-on-write). */
  is_override: boolean;
  doc_count: number;
  last_generated: string | null;
  config_count: number;
}

/** One row of the profile->persona mapping grid. */
export interface ProfileMapping {
  id: number;
  sf_profile: string;
  persona_id: number;
  persona_key: string;
  persona_name: string;
  source: 'inferred' | 'human' | 'agent_confirmed';
  confidence: number;
  rationale: string | null;
  status: 'proposed' | 'confirmed' | 'rejected';
  updated_at: string;
}

export type PersonaConfigType = 'glossary' | 'suppress' | 'inject' | 'emphasis' | 'section' | 'tone';

export interface PersonaConfigEntry {
  id: number;
  persona_id: string;
  object_api_name: string | null;
  field_api_name: string | null;
  config_type: PersonaConfigType;
  payload: unknown;
  source: 'inferred' | 'human' | 'agent_confirmed';
  confidence: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export const fetchPersonas = (tenantId: number) =>
  get<{ personas: PersonaCatalogEntry[] }>(`/api/personas?tenant=${tenantId}`);
export const createPersona = (input: {
  tenantId: number;
  key: string;
  name: string;
  definition: string;
}) => post<{ ok: boolean; id: number }>('/api/personas', input);
export const editPersona = (
  id: number,
  input: { tenantId: number; name?: string; definition?: string; isActive?: boolean },
) => post<{ ok: boolean; persona: PersonaCatalogEntry }>(`/api/personas/${id}`, input);
export const deactivatePersona = (id: number, tenantId: number) =>
  post<{ ok: boolean; persona: PersonaCatalogEntry }>(`/api/personas/${id}/deactivate`, { tenantId });

export const fetchProfileMappings = (tenantId: number) =>
  get<{ mappings: ProfileMapping[] }>(`/api/profile-persona-map?tenant=${tenantId}`);
export const correctMapping = (id: number, input: { tenantId: number; personaId: number }) =>
  post<{ ok: boolean }>(`/api/profile-persona-map/${id}`, input);
export const confirmAllMappings = (tenantId: number) =>
  post<{ ok: boolean; confirmed: number }>('/api/profile-persona-map/confirm-all', { tenantId });
export const fetchPersonaConfig = (personaId: string, tenantId: number) =>
  get<{ config: PersonaConfigEntry[] }>(
    `/api/personas/${encodeURIComponent(personaId)}/config?tenant=${tenantId}`,
  );
export const addPersonaConfig = (
  personaId: string,
  input: {
    tenantId: number;
    configType: PersonaConfigType;
    payload: unknown;
    objectApiName?: string;
    fieldApiName?: string;
  },
) =>
  post<{ ok: boolean; id: number }>(`/api/personas/${encodeURIComponent(personaId)}/config`, input);
export const updatePersonaConfig = (id: number, input: { payload?: unknown; active?: boolean }) =>
  post<{ ok: boolean }>(`/api/personas/config/${id}`, input);

// ---- Release notes + change log (digadop-ai#40 v1) ----

export interface ReleaseNoteSummary {
  id: string; // uuid
  org_alias: string;
  kind: 'per-refresh' | 'daily' | 'weekly';
  refresh_run_id: string | null;
  period_start: string;
  period_end: string;
  items_in_run: number;
  model: string | null;
  generated_at: string;
  has_markdown: boolean;
}

export interface ReleaseNote extends Omit<ReleaseNoteSummary, 'has_markdown'> {
  source_run_ids: string[];
  markdown: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface ChangeLogEntry {
  record_id: string;
  sf_object: string;
  change_kind: 'create' | 'update' | 'delete' | 'undelete';
  sync_at: string;
  refresh_run_id: string | null;
  source_lastmodifiedby: string | null;
  org_alias: string;
}

export const fetchReleaseNotes = (org?: string) =>
  get<{ notes: ReleaseNoteSummary[] }>(
    `/api/release-notes${org ? `?org=${encodeURIComponent(org)}` : ''}`,
  );
export const fetchReleaseNote = (id: string) =>
  get<{ note: ReleaseNote }>(`/api/release-notes/${id}`);
export const fetchChangeLog = (filters: { org?: string; from?: string; to?: string } = {}) => {
  const qs = new URLSearchParams();
  if (filters.org) qs.set('org', filters.org);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  const suffix = qs.toString();
  return get<{ changes: ChangeLogEntry[] }>(`/api/change-log${suffix ? `?${suffix}` : ''}`);
};
